import { Fragment } from 'react'

import { isMacOS } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

export const CommandKeyDecoration = ({ shortcut }: { shortcut: string }) => {
  const isCombo = shortcut.includes('+')
  const isSequence = shortcut.includes('>')

  if (isCombo) {
    const split = shortcut.split('+')
    return (
      <div className="flex items-center gap-1">
        {split.map((key, index) => (
          <ShortcutKey key={`${key}-${index}`} shortcutKey={key} />
        ))}
      </div>
    )
  }

  if (isSequence) {
    const split = shortcut.split(' > ')
    return (
      <div className="flex items-center gap-0.5">
        {split.map((key, index) => (
          <Fragment key={`${key}-${index}`}>
            {index > 0 && (
              <span className="px-0.5 text-xs font-normal text-secondary">
                then
              </span>
            )}
            <ShortcutKey shortcutKey={key} />
          </Fragment>
        ))}
      </div>
    )
  }

  return <ShortcutKey shortcutKey={shortcut} />
}

// Known keys that have special icon/symbol rendering
export type ShortcutKey =
  | 'shift'
  | 'slash'
  | 'comma'
  | 'enter'
  | 'tab'
  | 'esc'
  | 'up'
  | 'down'
  | 'meta'
  // Allows arbitrary keys (single letters, combos like 'shift+c', sequences like 'g > a')
  | (string & {})

const KEY_SYMBOLS: Record<string, string> = {
  shift: '⇧',
  slash: '/',
  comma: ',',
  enter: 'ENTER',
  tab: 'TAB',
  esc: 'ESC',
  up: '↑',
  down: '↓',
}

const ShortcutKey = ({ shortcutKey }: { shortcutKey: string }) => {
  const mapped =
    shortcutKey === 'meta' ? (isMacOS() ? '⌘' : '⌃') : KEY_SYMBOLS[shortcutKey]
  const formattedShortcutKey = mapped ?? shortcutKey.toUpperCase()
  const limitWidth = !mapped

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-md border border-default px-1 py-1 text-xs font-medium leading-none text-secondary',
        limitWidth && 'w-5',
        formattedShortcutKey === 'i' && 'font-mono',
      )}
    >
      {formattedShortcutKey}
    </div>
  )
}
